import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { postProductReview, uploadReviewImages } from "../../slices/reviews";
import { FaStar, FaTimes, FaCamera } from 'react-icons/fa'; // Icons for better UI

// --- CSS-in-JS Styles Object (unchanged, but now actively used) ---
const styles = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)', 
        display: 'flex', // Add display flex here for centering
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: '#fff',
        padding: '30px',
        borderRadius: '12px',
        maxWidth: '550px',
        width: '90%',
        boxShadow: '0 15px 40px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        maxHeight: '90vh', // Ensure it doesn't take up the full screen height
        overflowY: 'auto', // Allow scrolling for smaller screens
    },
    modalHeader: {
        fontSize: '22px',
        fontWeight: 'bold',
        marginBottom: '25px',
        borderBottom: '1px solid #eee',
        paddingBottom: '15px',
        textAlign: 'center',
        color: '#333',
    },
    formGroup: {
        marginBottom: '20px',
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: 'bold',
        color: '#444',
        fontSize: '15px',
    },
    input: {
        width: '100%',
        padding: '12px',
        borderRadius: '6px',
        border: '1px solid #ddd',
        boxSizing: 'border-box',
        fontSize: '15px',
    },
    textarea: {
        width: '100%',
        padding: '12px',
        borderRadius: '6px',
        border: '1px solid #ddd',
        minHeight: '120px',
        resize: 'vertical',
        boxSizing: 'border-box',
        fontSize: '15px',
    },
    ratingInputContainer: {
        display: 'flex',
        justifyContent: 'center',
        fontSize: '34px',
        marginBottom: '10px',
        gap: '8px',
    },
    starInput: {
        color: '#d4d4d4',
        cursor: 'pointer',
        transition: 'color 0.2s ease-in-out',
    },
    starInputActive: {
        color: '#ffc107', // Gold color for active
    },
    starInputHover: {
        color: '#ffeb3b', // Lighter gold on hover
    },
    submitButton: {
        backgroundColor: '#E53E3E', // A nice red/primary color
        color: 'white',
        border: 'none',
        padding: '12px 25px',
        borderRadius: '6px',
        fontSize: '17px',
        fontWeight: '600',
        cursor: 'pointer',
        width: '100%',
        marginTop: '20px',
        transition: 'background-color 0.3s ease',
    },
    submitButtonDisabled: {
        backgroundColor: '#ccc',
        cursor: 'not-allowed',
    },
    closeButton: {
        position: 'absolute',
        top: '15px',
        right: '15px',
        background: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: '#aaa',
        transition: 'color 0.2s ease',
    },
    // closeButtonHover (Handled with onMouseEnter/onMouseLeave)

    // --- Image Upload Specific Styles ---
    fileInputContainer: {
        border: '2px dashed #e0e0e0',
        borderRadius: '8px',
        padding: '20px',
        textAlign: 'center',
        cursor: 'pointer',
        backgroundColor: '#fdfdfd',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
    },
    fileInputContainerHover: {
        borderColor: '#E53E3E',
        backgroundColor: '#fff7f5',
    },
    fileInput: {
        display: 'none',
    },
    uploadText: {
        color: '#888',
        fontSize: '15px',
        fontWeight: 'normal',
        marginBottom: '10px',
    },
    uploadIcon: {
        fontSize: '40px',
        color: '#bbb',
        marginBottom: '10px',
    },
    imagePreviewsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginTop: '15px',
    },
    imagePreviewWrapper: {
        position: 'relative',
        width: '80px',
        height: '80px',
        borderRadius: '6px',
        overflow: 'hidden',
        border: '1px solid #ddd',
    },
    previewImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    removeImageButton: {
        position: 'absolute',
        top: '2px',
        right: '2px',
        backgroundColor: 'rgba(255, 0, 0, 0.7)',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '12px',
        cursor: 'pointer',
        padding: 0,
        lineHeight: 1,
        zIndex: 10,
    }
}


const PostReviewForm = ({ productId, onClose }) => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.reviews);

    const [rating, setRating] = useState(0);
    const [title, setTitle] = useState("");
    const [comment, setComment] = useState("");
    const [images, setImages] = useState([]);
    const [hoverRating, setHoverRating] = useState(0);
    const [isHoveringClose, setIsHoveringClose] = useState(false);
    const [isHoveringFileInput, setIsHoveringFileInput] = useState(false);
    const fileInputRef = useRef(null);

    const maxImages = 5;

    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const totalFiles = [...images, ...selectedFiles];
        setImages(totalFiles.slice(0, maxImages));
    };

    const handleRemoveImage = (indexToRemove) => {
        setImages(images.filter((_, index) => index !== indexToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!rating || !title || !comment) return;

        const reviewData = { product_id: productId, rating, title, comment };
        
        try {
            const review = await dispatch(postProductReview(reviewData)).unwrap();

            console.log(review)

            if (images.length > 0) {
              for (let image of images) {
                const formData = new FormData();
                formData.append("file", image);
                await dispatch(uploadReviewImages({ reviewId: review.data.id, formData })).unwrap();
              }
            }

            setRating(0);
            setTitle("");
            setComment("");
            setImages([]);
            onClose();
        } catch (error) {
            console.error("Review submission failed:", error);
        }
    };

    const isSubmitDisabled = loading || !rating || !title.trim() || !comment.trim();

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
                
                <button
                    onClick={onClose}
                    style={{
                        ...styles.closeButton,
                        ...(isHoveringClose ? styles.closeButtonHover : {})
                    }}
                    onMouseEnter={() => setIsHoveringClose(true)}
                    onMouseLeave={() => setIsHoveringClose(false)}
                >
                    <FaTimes />
                </button>
                
                <h3 style={styles.modalHeader}>Write a Review</h3>

                <form onSubmit={handleSubmit}>
                    
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Your Rating *</label>
                        <div style={styles.ratingInputContainer}>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <FaStar key={star}
                                    style={{
                                        ...styles.starInput,
                                        ...(star <= (hoverRating || rating) ? styles.starInputActive : {}),
                                        ...(star <= hoverRating ? styles.starInputHover : {})
                                    }}
                                    onClick={() => setRating(star)}
                                    onMouseEnter={() => setHoverRating(star)}
                                    onMouseLeave={() => setHoverRating(0)}
                                />
                            ))}
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Review Title *</label>
                        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                            style={styles.input} maxLength={100} required />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Your Comment *</label>
                        <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                            style={styles.textarea} required />
                    </div>

                    {/* Image Upload */}
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Images (Optional - Max {maxImages})</label>
                        
                        {/* Custom File Input/Dropzone */}
                        <div
                            style={{
                                ...styles.fileInputContainer,
                                ...(isHoveringFileInput ? styles.fileInputContainerHover : {})
                            }}
                            onClick={() => fileInputRef.current.click()}
                            onMouseEnter={() => setIsHoveringFileInput(true)}
                            onMouseLeave={() => setIsHoveringFileInput(false)}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                multiple
                                accept="image/*"
                                onChange={handleFileSelect}
                                style={styles.fileInput}
                            />
                            <FaCamera style={styles.uploadIcon} />
                            <p style={styles.uploadText}>Click to upload up to {maxImages} images</p>
                        </div>
                        
                        {/* Image Previews */}
                        {images.length > 0 && (
                            <div style={styles.imagePreviewsContainer}>
                                {images.map((file, idx) => (
                                    <div key={idx} style={styles.imagePreviewWrapper}>
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt={`Preview ${idx + 1}`}
                                            style={styles.previewImage}
                                        />
                                        <button type="button" onClick={() => handleRemoveImage(idx)}
                                            style={styles.removeImageButton}>
                                            &times;
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button type="submit" disabled={isSubmitDisabled}
                        style={{
                            ...styles.submitButton,
                            ...(isSubmitDisabled ? styles.submitButtonDisabled : {})
                        }}
                    >
                        {loading ? "Submitting..." : "Submit Review"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default PostReviewForm;